import { useMemo } from 'react';
import { useOrgUnits } from '../../hooks/useOrgUnits';
import type { OrgUnit, OrgUnitType } from '../../types/orgUnit';

interface UserGroupsChecklistProps {
  customerId: string;
  selectedOrgUnitIds: string[];
  onChange: (ids: string[]) => void;
}

interface DisplayUnit {
  org_unit_id: string;
  type: OrgUnitType;
  label: string;         // breadcrumb-formatted
  sortKey: string;       // for stable ordering
}

/**
 * Flat checkbox list of a customer's region + district org-units with breadcrumb labels.
 * Mirrors SiteAccessChecklist visually; replaces individual-site-grant semantics with
 * group-membership assignment (Phase 3b, djin-cloud#53).
 */
export function UserGroupsChecklist({
  customerId,
  selectedOrgUnitIds,
  onChange,
}: UserGroupsChecklistProps) {
  const { data: units, isLoading } = useOrgUnits({ customer_id: customerId });

  const displayUnits = useMemo<DisplayUnit[]>(() => {
    if (!units) return [];
    const byId = new Map<string, OrgUnit>(units.map((u) => [u.org_unit_id, u]));
    const rows: DisplayUnit[] = units.map((u) => {
      if (u.type === 'region' || u.parent_id === null) {
        return {
          org_unit_id: u.org_unit_id,
          type: u.type,
          label: u.name,
          sortKey: `${u.name}  ${u.type} ${u.name}`,
        };
      }
      const parent = u.parent_id ? byId.get(u.parent_id) : null;
      const parentName = parent?.name ?? '(orphan)';
      return {
        org_unit_id: u.org_unit_id,
        type: u.type,
        label: `${parentName} / ${u.name}`,
        sortKey: `${parentName} ${u.type} ${u.name}`,
      };
    });
    return rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [units]);

  const selectedSet = useMemo(() => new Set(selectedOrgUnitIds), [selectedOrgUnitIds]);
  const allSelected = displayUnits.length > 0 && displayUnits.every((u) => selectedSet.has(u.org_unit_id));

  const toggleAll = () => {
    onChange(allSelected ? [] : displayUnits.map((u) => u.org_unit_id));
  };

  const toggle = (id: string) => {
    onChange(
      selectedSet.has(id)
        ? selectedOrgUnitIds.filter((i) => i !== id)
        : [...selectedOrgUnitIds, id]
    );
  };

  if (isLoading) {
    return <div className="text-sm text-ems-text-muted py-2">Loading groups...</div>;
  }

  if (displayUnits.length === 0) {
    return (
      <div className="text-sm text-ems-text-muted py-2">
        No groups defined yet. An admin can create regions and districts at <code>/admin/org-units</code>.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={toggleAll}
        className="text-xs text-ems-green hover:underline"
      >
        {allSelected ? 'Deselect All' : 'Select All'}
      </button>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {displayUnits.map((unit) => (
          <label
            key={unit.org_unit_id}
            className="flex items-center gap-2 py-1 px-2 rounded hover:bg-ems-bg-card-hover cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedSet.has(unit.org_unit_id)}
              onChange={() => toggle(unit.org_unit_id)}
              className="rounded border-ems-border text-ems-green focus:ring-ems-green/20"
            />
            <span className="text-sm text-ems-text-primary">{unit.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
